import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { allHeldTypesFlagDeny, anyHeldTypeAllowed, COMPANY_TITLE, IS_STAGING, LoanFieldProps } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig } from 'services'
import { svgLoading } from 'stories/assets'
import { useTitle } from 'utils'
import { getLoanFieldSubMenus } from 'utils/loanFieldSubMenus.util'
import { setLoanNumber } from 'utils/setLoanNumber'

import { SubmenuItem, subMenus } from '../../../pages/Admin/AdminTools/Configuration/Features/constant'
import { NotePageType } from '../Servicing/Notes/types'
import { getComponent } from './ComponentMap'
import { CustomInformationPage } from './CustomInformation'

export interface ConfigurablePageConfig {
  title: string
  url: string
  submenus: (string | SubmenuItem)[]
  value?: boolean
  permissions?: string[]
}

interface ConfigurablePageProps {
  config?: ConfigurablePageConfig
}

// Page permission and vendor requirements per submenu key (account type / loan status come from features config)
const menuPermissions: Record<string, { permissions?: string[]; requireVendor?: string }> = {
  // Servicing permissions
  servicingInformation: { permissions: ['Loan.Servicing.ServicingInformation'] },
  trustLedger: { permissions: ['Loan.Servicing.TrustLedger'] },
  servicingDocument: { permissions: ['Loan.Servicing.Documents'] },
  loanExtensions: { permissions: ['Loan.Servicing.Extension'] },
  participation: { permissions: ['Loan.Servicing.Participation'] },
  defaultTracking: { permissions: ['Loan.Servicing.DefaultTracking'] },
  creditReporting: { permissions: ['Servicing.CreditReporting'] },
  collectionCalls: { permissions: ['Loan.Servicing.CollectionCalls'] },
  textMessages: { permissions: ['Loan.Servicing.TextMessages'] },
  notesForServicing: { permissions: [] },
  globalNotes: { permissions: ['Loan.Servicing.Notes'] },
  payoff: { permissions: ['Loan.Servicing.Payoff'] },
  reinstatement: { permissions: ['Loan.Servicing.Reinstatement'] },
  statements: { permissions: ['Loan.Servicing.Documents'] },
  rateChanges: { permissions: ['Loan.Servicing.ServicingInformation'] },
  loanCharges: { permissions: ['Loan.Servicing.LoanCharges'] },
  penalties: { permissions: ['Loan.Servicing.Penalties'] },
  tasks: { permissions: ['Task'] },
  fci: { requireVendor: 'Fci' },
  setPoint: { requireVendor: 'Setpoint' },

  // Underwriting permissions
  appraisalReview: { permissions: ['Loan.Underwriting'] },
  expirationDates: { permissions: ['Loan.Underwriting'] },
  underwritingSummary: { permissions: ['Loan.Underwriting'] },
  appraisalInformation: { permissions: ['Loan.Underwriting', 'Loan.Underwriting.AppraisalInformation'] },
  dataVerifySSA: {
    permissions: ['Loan.Underwriting'],
    requireVendor: 'DataverifySSA',
  },
  dataVerify4506: {
    permissions: ['Loan.Underwriting'],
    requireVendor: 'DataverifyTAX',
  },
  dataVerifyDrive: {
    permissions: ['Loan.Underwriting'],
    requireVendor: 'DataverifyDrive',
  },
  lexis: {
    permissions: ['Loan.Underwriting'],
    requireVendor: 'Lexisnexis',
  },
  nyTransactions: { permissions: ['Loan.Underwriting'] },
  escrowHoldbackAgreement: { permissions: ['Loan.Underwriting'] },

  // Closing Screen
  lightningDocs: { requireVendor: 'LightningDocs' },
  automateLoanDocs: { requireVendor: 'AutomateLoanDocs' },
  goDocs: { requireVendor: 'GoDocs' },
  docMagic: { requireVendor: 'DocMagic' },

  // LoanInTake permissions
  nationFlood: {
    permissions: ['Loan.Underwriting'],
  },
  orderOuts: { permissions: ['Loan.Underwriting'] },
  loanIntakeDocuments: { permissions: ['Loan.Underwriting'] },
  loanDetailValidation: { permissions: ['Loan.Underwriting'] },
  loanActions: { permissions: ['Loan.Actions'] },
}

export default function ConfigurablePage({ config: configProp }: ConfigurablePageProps) {
  const [loading, setLoading] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState('')
  const [pageConfigurations, setPageConfigurations] = useState<Record<string, ConfigurablePageConfig>>({})
  const [accessDenied, setAccessDenied] = useState(false)
  const params = useParams<{ pageId?: string; loanNumber?: string; menuItem?: string }>()
  const history = useHistory()
  const location = useLocation()
  const { hasPermission } = usePermissions()

  // Get menu from URL query param
  const getMenuFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('menu')
  }

  // Update URL with menu query param
  const updateUrlWithMenu = (menuItem: string) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('menu', menuItem)
    history.replace({ search: searchParams.toString() })
  }

  const { auth, vendor, loanDetail, loanFields } = useSelector((state: any) => ({
    auth: state.auth,
    vendor: state.vendor,
    loanDetail: state.loanDetail,
    loanFields: state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
  }))

  const loanFieldSubMenus = useMemo(() => getLoanFieldSubMenus(loanFields), [loanFields])

  const allSubMenus: Record<string, { title: string }> = useMemo(
    () => ({ ...subMenus, ...loanFieldSubMenus }),
    [loanFieldSubMenus],
  )

  // Check if a menu item is allowed based on page permissions and vendor status
  const isMenuAllowed = (menuKey: string): boolean => {
    const config = menuPermissions[menuKey]
    if (!config) return true // No restrictions

    // Check permissions
    if (config.permissions) {
      const hasAllPermissions = config.permissions.every((perm) => hasPermission(perm))
      if (!hasAllPermissions) return false
    }

    // Check vendor requirement
    if (config.requireVendor) {
      if (!vendor?.[config.requireVendor]) return false
    }

    return true
  }

  /** Submenu-level loan status / account type from admin features config (empty = no restriction) */
  const isSubmenuAllowedByFeatureConfig = (submenuItem: SubmenuItem): boolean => {
    const statusKeys = submenuItem.loanStatus
    if (statusKeys) {
      const current = loanDetail?.loanStatus
      if (!current || statusKeys[current] === false) return false
    }

    const accountKeys = submenuItem.accountTypes
    // Deny only when every held role is explicitly false for this submenu.
    if (accountKeys && allHeldTypesFlagDeny(auth?.profile, accountKeys)) return false

    return true
  }

  // Normalize submenus to new format (array of objects)
  const normalizeSubmenus = (submenus?: (string | SubmenuItem)[]): SubmenuItem[] => {
    if (!submenus) return []
    return submenus.map((item) => {
      if (typeof item === 'string') {
        return { key: item, isExternal: false }
      }
      return item
    })
  }

  useEffect(() => {
    getAdminConfig('features').then((data) => {
      setPageConfigurations(data)
    })
  }, [])

  // Enforce quick link account-type permission: if user opened via direct URL but their account type is not allowed, redirect
  useEffect(() => {
    if (!params.pageId || configProp) return
    const feature = pageConfigurations[params.pageId] as
      | (ConfigurablePageConfig & { value?: boolean; permissions?: string[] })
      | undefined

    if (!feature) return

    const enabled = feature.value !== false
    const allowedAccountTypes = feature.permissions || []
    const hasAccountTypeAccess = anyHeldTypeAllowed(auth?.profile, allowedAccountTypes)

    if (!enabled || !hasAccountTypeAccess) {
      setAccessDenied(true)
      toast.error('You do not have access to this page.')
      history.replace('/pipeline')
    }
  }, [params.pageId, configProp, pageConfigurations, auth?.profile, history])

  // Get config from props or URL params
  const config = useMemo(() => {
    if (configProp) return configProp

    // Try to get config from URL params
    const pageId = params.pageId
    const menuItem = params.menuItem

    // If menuItem is present, create a single-menu config
    if (menuItem && allSubMenus[menuItem]) {
      const menuTitle = allSubMenus[menuItem].title
      return {
        title: menuTitle,
        url: pageId || menuItem,
        submenus: [menuItem],
      }
    }

    if (pageId && pageConfigurations[pageId]) {
      return pageConfigurations[pageId]
    }

    // Default config if nothing found
    return {
      title: 'Custom Page',
      url: 'custom_page',
      submenus: [],
    }
  }, [configProp, params.pageId, params.menuItem, pageConfigurations, allSubMenus])

  // Submenus that always open as external links with custom routes
  const externalMenuRoutes: Record<string, string> = {
    tasks: `/tasks/loan/${params.loanNumber}`,
    reminders: `/reminders/${params.loanNumber}`,
    loanStructure: `/loan_process/structure/${params.loanNumber}`,
    loanApplication: `/loan_process/application/${params.loanNumber}`,
    loanSubmission: `/loan_process/submit/${params.loanNumber}`,
  }

  // Build menu items from config submenus (preserving order)
  const leftMenuItems = useMemo(() => {
    const menuItems: Record<string, { title: string; isExternal?: boolean }> = {}
    const normalizedSubmenus = normalizeSubmenus(config.submenus)

    // Iterate through submenus array which already has the desired order
    normalizedSubmenus.forEach((submenuItem) => {
      const builtInSubmenu = subMenus[submenuItem.key as keyof typeof subMenus]
      const loanFieldSubmenu = loanFieldSubMenus[submenuItem.key]
      if (!builtInSubmenu && !loanFieldSubmenu) return

      if (submenuItem.key === 'compliance' && !IS_STAGING) return

      // Check page permission / vendor restrictions (built-in submenus only)
      if (builtInSubmenu && !isMenuAllowed(submenuItem.key)) return
      if (!isSubmenuAllowedByFeatureConfig(submenuItem)) return

      // Force external for certain menus (e.g., tasks)
      const isExternal = submenuItem.isExternal || !!externalMenuRoutes[submenuItem.key]

      const defaultTitle = builtInSubmenu?.title || loanFieldSubmenu?.title || submenuItem.key
      menuItems[submenuItem.key] = {
        title: submenuItem.title || defaultTitle,
        isExternal: loanFieldSubmenu ? false : isExternal,
      }
    })

    const loanFieldMenuItems = Object.fromEntries(
      Object.entries(menuItems).filter(([key]) => loanFieldSubMenus[key]),
    ) as Record<string, { title: string; isExternal?: boolean }>

    // Apply permissions filtering to built-in submenus only
    removeNonPagePermissions(menuItems as any, ['pipeline', config.url], true)
    Object.assign(menuItems, loanFieldMenuItems)

    return menuItems
  }, [
    config.submenus,
    config.url,
    params.loanNumber,
    auth,
    vendor,
    loanDetail?.loanStatus,
    hasPermission,
    loanFieldSubMenus,
  ])

  // Get ordered menu keys based on the config.submenus array order
  const orderedMenuKeys = useMemo(() => {
    const normalizedSubmenus = normalizeSubmenus(config.submenus)
    return normalizedSubmenus.map((item) => item.key).filter((key) => leftMenuItems[key])
  }, [config.submenus, leftMenuItems])

  const getExternalMenuUrl = (menuItem: string) => {
    const customRoute = externalMenuRoutes[menuItem]
    return customRoute || `/configurablePage/${params.pageId || config.url}/${menuItem}/${params.loanNumber}`
  }

  const setMenu = (menuItem: string, updateUrl = true) => {
    const menuConfig = leftMenuItems[menuItem]

    // If it's an external link, open in new tab and leave the current page alone
    if (menuConfig?.isExternal) {
      window.open(getExternalMenuUrl(menuItem), '_blank')
      return
    }

    setSelectedMenu(menuItem)
    if (updateUrl) {
      updateUrlWithMenu(menuItem)
    }
  }

  const renderFragment = useMemo(() => {
    if (!selectedMenu) return <div>Coming Soon...</div>

    if (loanFieldSubMenus[selectedMenu]) {
      return <CustomInformationPage menuKey={selectedMenu} setLoading={setLoading} />
    }

    const Component = getComponent(selectedMenu)

    const notePageType =
      selectedMenu === 'servicingNotes'
        ? NotePageType.SERVICING
        : selectedMenu === 'reoNotes'
        ? NotePageType.REO
        : NotePageType.GLOBAL

    const hud1PageType = selectedMenu === 'hud1Page2' ? 'hud1Page2' : 'hud1A'

    return (
      <Suspense
        fallback={
          <div className="flex justify-center items-center p-8">
            <img src={svgLoading} className="inline w-8 h-8 text-white animate-spin" />
          </div>
        }
      >
        <Component
          // Several menu keys share one component (the three notes screens, both HUD1 pages). Keyed
          // by menu so switching between them remounts instead of reusing the instance and carrying
          // the previous screen's state across on nothing but a prop change.
          key={selectedMenu}
          setLoading={setLoading}
          child={true}
          forBorrower={false}
          notePageType={notePageType}
          pageType={hud1PageType}
          title={leftMenuItems[selectedMenu]?.title}
        />
      </Suspense>
    )
  }, [selectedMenu, loanFieldSubMenus, leftMenuItems])

  useEffect(() => {
    // Only set initial menu if no menu is currently selected or if the selected menu is no longer valid
    if (orderedMenuKeys.length > 0 && (!selectedMenu || !orderedMenuKeys.includes(selectedMenu))) {
      // Prefer an in-page menu. External-only pages should open a new tab and leave this one put.
      const menuFromUrl = getMenuFromUrl()
      const preferredDefault =
        menuFromUrl && orderedMenuKeys.includes(menuFromUrl)
          ? menuFromUrl
          : params.pageId === 'servicing' && orderedMenuKeys.includes('servicingInformation')
          ? 'servicingInformation'
          : orderedMenuKeys.find((key) => !leftMenuItems[key]?.isExternal) || orderedMenuKeys[0]

      if (leftMenuItems[preferredDefault]?.isExternal) {
        window.open(getExternalMenuUrl(preferredDefault), '_blank')
        // Landing here from Overview (no menuItem in the path) would leave a blank shell tab —
        // send the user back to Overview so the original tab does not change destination.
        if (!params.menuItem && params.loanNumber) {
          history.replace(`/loan_process/overview/${params.loanNumber}`)
        }
      } else {
        setMenu(preferredDefault, !(menuFromUrl && orderedMenuKeys.includes(menuFromUrl)))
      }
    }
    setLoanNumber()
  }, [orderedMenuKeys])

  // Set browser tab title
  useEffect(() => {
    useTitle(`${params.loanNumber} - ${config.title} - ${COMPANY_TITLE}`)
  }, [config.title])

  const renderMenu = (menus: Record<string, { title: string; isExternal?: boolean }>, orderedKeys: string[]) => {
    return (
      <ul className="sidebar-items flex flex-col p-2 pb-6 sm:p-4 sm:pb-10">
        {orderedKeys.map((item: string, index) => {
          const isActive = selectedMenu === item
          const menuConfig = menus[item]
          return (
            <li
              key={index}
              onClick={() => {
                setMenu(item)
              }}
              className="py-2"
            >
              <p
                className={`min-w-0 cursor-pointer flex items-center gap-2 hover:underline ${
                  isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'
                }`}
              >
                <span className="min-w-0 break-words">
                  {index + 1}. {menuConfig?.title}
                </span>
                {menuConfig?.isExternal && (
                  <span className="text-shade-blue">
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                )}
              </p>
            </li>
          )
        })}
      </ul>
    )
  }

  const showSidebar = Object.keys(leftMenuItems).length > 1
  const isFciDetailOnly = orderedMenuKeys.length === 1 && orderedMenuKeys[0] === 'fciDetail'

  if (accessDenied) {
    return null
  }

  return (
    <div className="ConfigurablePage-container px-2 py-3 sm:px-4 sm:py-6">
      <LayoutLoading show={loading} />
      <Overview title={config.title} showStatusChannel={true} />
      <div className="m-auto grid max-w-screen-2xl grid-cols-12 md:gap-6">
        {showSidebar && (
          <div className="sidebar-left col-span-12 mb-4 min-w-0 shrink-0 rounded bg-white shadow1 md:col-span-3">
            {renderMenu(leftMenuItems, orderedMenuKeys)}
          </div>
        )}
        <div
          className={`relative h-fit min-w-0 content-right col-span-12 ${
            showSidebar ? 'md:col-span-9' : 'md:col-span-12'
          } ${
            isFciDetailOnly ? '' : 'mb-4 rounded bg-white p-2 shadow1 sm:p-4 md:p-7'
          }`}
        >
          <div className="min-w-0 overflow-x-auto">{renderFragment}</div>
        </div>
      </div>
    </div>
  )
}
